import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { z } from "zod";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Textarea } from "components/ui/Textarea";
import { useCreateTask } from "features/comunications/hooks/useCreateTask";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./AddReminderModal.module.css";

const formSchema = z.object({
  dueDate: z.string().min(1, { message: "Please select due date." }),
  instruction: z.string().min(1, { message: "Please provide instruction." }),
});

export const AddReminderModal = ({ patient, buttonText = "Add Reminder" }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: createTask, isPending } = useCreateTask();
  const { data: currentUser, isLoading: userLoading } = useStaffInfo();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dueDate: "",
      instruction: "",
    },
  });

  const onSubmit = (data) => {
    if (!patient || !currentUser) {
      toast.error("Patient or user information not available");

      return;
    }

    const dueDateForApi = moment(data.dueDate, "DD-MM-YYYY").format(
      "YYYY-MM-DD HH:mm"
    );

    createTask(
      {
        dueDate: dueDateForApi,
        patientId: patient.id,
        assigneeIds: currentUser.staffMember?.id
          ? [currentUser.staffMember.id]
          : [],
        priority: 0,
        status: "Open",
        title: `Reminder: ${data.instruction}`,
        assigneeComments: "",
        reminder: true,
        tenant: localStorage.getItem("kinesin-tenant"),
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          form.reset();
        },
      }
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
    form.reset();
  };

  const handleSubmit = form.handleSubmit(onSubmit, onInvalidSubmit("AddReminderModal"));

  return (
    <>
      <Button
        variant="outline"
        disabled={userLoading}
        onClick={() => setIsModalOpen(true)}
      >
        {userLoading ? "Loading..." : buttonText}
      </Button>

      <Dialog
        open={isModalOpen}
        title="Add Patient Reminder"
        onClose={closeModal}
        width={500}
      >
        <FormProvider {...form}>
          <form onSubmit={handleSubmit} className={style.form}>
            <FormField name="patientName" label="Patient">
              <CustomInput
                value={
                  patient ? `${patient.firstName} ${patient.surname}` : "N/A"
                }
                disabled
              />
            </FormField>

            <FormField name="dueDate" label="Due Date">
              <Controller
                name="dueDate"
                control={form.control}
                render={({ field }) => (
                  <CustomCalendar
                    value={
                      field.value
                        ? moment(field.value, "DD-MM-YYYY").toDate()
                        : null
                    }
                    onChange={(date) =>
                      field.onChange(
                        date ? moment(date).format("DD-MM-YYYY") : ""
                      )
                    }
                    placeholder="Select due date"
                    minDate={new Date()}
                  />
                )}
              />
            </FormField>

            <FormField name="instruction" label="Reminder Instruction">
              <Textarea
                {...form.register("instruction")}
                placeholder="Enter reminder instruction..."
                rows={4}
              />
            </FormField>
          </form>
        </FormProvider>
        <DialogFooter>
          <Button variant="outline" onClick={closeModal} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Creating..." : "Create Reminder"}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};
